import React from 'react';
import PropTypes from 'prop-types';
import { isEmpty, reject, isNil } from 'lodash-es';
import styles from './styles.scss';

import Map from './Map';
import PhotoGrid from './PhotoGrid';
import {
  mapProps,
  photoListProps,
  pageProps
} from '../../../props/feed-prop-types';

// Image entry types
const ACTIVITY = 'activity';
const GROUP_ACTIVITY = 'group_activity';
const POST = 'post';

// Maximum # of photos allowed for each kind of entry (this does not include the map image)
const ACTIVITY_MAX_PHOTO_COUNT = 5;
const GROUP_ACTIVITY_MAX_PHOTO_COUNT = 4;
const POST_MAX_PHOTO_COUNT = 1;

// Styling class for the container that houses all the image entries
// Note: size count includes the activity map if its available
const imageEntriesContainerClass = ({
  entryType,
  totalNumEntryImages, // includes map image
  showMap
}) => {
  if (entryType === ACTIVITY) {
    switch (totalNumEntryImages) {
      case 1:
        return styles.images1Up;
      case 2:
        return styles.images2Up;
      case 3:
        return styles.images3Up;
      case 4:
        return styles.images4Up;
      case 5:
        return styles.images5Up;
      case 6:
        return styles.images6Up;
      default:
        return styles.images6Up;
    }
  } else if (entryType === GROUP_ACTIVITY) {
    return showMap ? styles.images1Up : '';
  } else if (entryType === POST) {
    return styles.images1Up;
  }
  return '';
};

const PhotosAndMapImage = ({
  entryType,
  activityId,
  activityMap,
  photoList,
  isRoutable,
  isVirtual,
  isCommute,
  workoutType,
  feedName
}) => {
  const isGroupActivity = entryType === GROUP_ACTIVITY;
  const isPost = entryType === POST;

  // maps are shown for all entries except for posts
  const showMap = !isEmpty(activityMap) && !isPost;
  const showPhotoGrid = (() => {
    if (isGroupActivity && showMap) {
      // for group activities, photos will be ignored if they are provided together with the map image
      // this scenario should technically not happen in real life.
      // multiple PhotosAndMapImage are used for group activities - eg. one for headline image and then one for every
      // matched activity that has photos in it.
      return false;
    }
    return !isEmpty(photoList) && !isPost;
  })();
  const showPost = isPost && !isEmpty(photoList);

  // map image is included as the total number of images being displayed (this is used for activity type entries)
  const totalNumEntryImages = showMap ? photoList.length + 1 : photoList.length;

  // maximum number of images that can be displayed
  const maxNumPhotosToShow = (() => {
    switch (entryType) {
      case ACTIVITY:
        // allow one extra photo if map image is not present
        return !showMap
          ? ACTIVITY_MAX_PHOTO_COUNT + 1
          : ACTIVITY_MAX_PHOTO_COUNT;
      case GROUP_ACTIVITY:
        return GROUP_ACTIVITY_MAX_PHOTO_COUNT;
      default:
        // POST
        return POST_MAX_PHOTO_COUNT;
    }
  })();

  const entryTypeClassName = (() => {
    switch (entryType) {
      case ACTIVITY:
        return styles.activityMap;
      case GROUP_ACTIVITY:
        return styles.groupActivityMap;
      default:
        // POST
        return styles.postImage;
    }
  })();

  const imagesContainerClass = imageEntriesContainerClass({
    entryType,
    totalNumEntryImages,
    showMap
  });

  const classNames = () => {
    let feedImageHeight = null;
    if (feedName === 'athlete_profile') {
      feedImageHeight = styles.athleteProfile;
    } else if (feedName === 'club') {
      feedImageHeight = styles.club;
    }
    return reject(
      [
        styles.photosAndMapImageWrapper,
        styles.entryImages,
        feedImageHeight,
        imagesContainerClass
      ],
      isNil
    ).join(' ');
  };

  return (
    <div className={classNames()} data-testid="entry-images">
      {/* Map Image - Activity and Group Activity */}
      {showMap && (
        <Map
          activityId={activityId}
          activityMap={activityMap}
          entryTypeClassName={entryTypeClassName}
          isRoutable={isRoutable}
          isVirtual={isVirtual}
          isCommute={isCommute}
          workoutType={workoutType}
        />
      )}
      {/* Photo Stream - Activity and Group Activity */}
      {showPhotoGrid && (
        <PhotoGrid
          maxNumPhotosToShow={maxNumPhotosToShow}
          photoList={photoList}
          entryTypeClassName={entryTypeClassName}
        />
      )}
      {/* Highlighted Image - Posts */}
      {showPost && (
        <PhotoGrid
          photoList={photoList}
          singleImageHighlighted={true}
          entryTypeClassName={entryTypeClassName}
        />
      )}
    </div>
  );
};

PhotosAndMapImage.propTypes = {
  entryType: PropTypes.oneOf([ACTIVITY, GROUP_ACTIVITY, POST]).isRequired,
  activityId: PropTypes.string.isRequired,
  isRoutable: PropTypes.bool,
  activityMap: mapProps,
  photoList: photoListProps,
  isVirtual: PropTypes.bool,
  isCommute: PropTypes.bool,
  workoutType: PropTypes.oneOf(['race', 'long_run', 'workout']),
  feedName: pageProps.isRequired
};

PhotosAndMapImage.defaultProps = {
  isRoutable: false,
  activityMap: {},
  photoList: [],
  isVirtual: false,
  isCommute: false,
  workoutType: null
};

export default PhotosAndMapImage;
